import React, { useEffect, useRef, useState } from "react";
import FileIcon from "assets/timesheet/file.svg";
import { FaTrash, FaEye, FaImage } from "react-icons/fa";
import CustomButton from "components/common/Button/CustomButton";
import { apiGetRequest, apiPostRequest } from "helpers/Requests";
import {
  URL_ROSTERING_DELETE_SINGLE_ATTACHMENT,
  URL_ROSTERING_GET_ALL_ATTACHMENTS,
  URL_ROSTERING_UPLOAD_DOCUMENTS,
} from "store/services/URL";
import { baseURLImage } from "Utils/utils";
import { toast } from "react-toastify";
import Loader from "components/common/Loader/Loader";
import { TimesheetAttachmentsTabSkeleton } from "../../../components/TimesheetRevamp/TimesheetDetailTabSkeletons";
import { uploadFilesOnVapour } from "Utils/uploadFileOnS3";
import {
  TimesheetDetailCard,
  TimesheetFieldLabel,
  TimesheetTabSectionHeader,
} from "../../../components/TimesheetRevamp/TimesheetTabPrimitives";
import { useSelector } from "react-redux";
import { getSpRosteringSetting, getSpSetting } from "store/selector/sp.selector";
import { getParticipantLabel } from "Constants/constant";

const stripFileExtension = (name) =>
  typeof name === "string" ? name.replace(/\.[^/.]+$/, "") : "";

const Attachments = ({ shiftDetails }) => {
  const spRosteringSetting = useSelector(getSpRosteringSetting);
  const spSetting = useSelector(getSpSetting);
  const careSector = spRosteringSetting?.care_sector ?? spSetting?.care_sector;
  const participantLabel = getParticipantLabel(careSector, false);
  const [attachmentsAndSignatures, setAttachmentsAndSignatures] =
    React.useState({
      attachments: [],
      signatures: null,
    });
  const [getAttachmentsLoader, setGetAttachmentsLoader] = React.useState(false);
  const fileInputRef = useRef(null);
  const [newUploadedImages, setNewUploadedImages] = useState([]);
  const [uploadingFilesData, setUploadedFiles] = useState([]);
  const [uploadFileLoader, setUploadFileLoader] = useState(false);

  const handleFileChange = async (event) => {
    const files = event.target.files;
    const maxFiles = 5;

    if (
      files.length +
        newUploadedImages.length +
        attachmentsAndSignatures?.attachments?.length >
      maxFiles
    ) {
      toast.error(`You can upload a maximum of ${maxFiles} images.`);
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
    const validFiles = Array.from(files).filter((file) =>
      allowedTypes.includes(file.type)
    );

    if (validFiles.length === 0) {
      toast.error("Please select valid image files (JPEG, PNG, JPG, GIF).");
      return;
    }
    setUploadFileLoader(true);
    try {
      const uploadedMeta = await uploadFilesOnVapour(validFiles);
      const nextMeta = [];
      const nextImages = [];
      uploadedMeta.forEach((meta, idx) => {
        if (meta) {
          nextMeta.push(meta);
          nextImages.push(validFiles[idx]);
        }
      });
      if (nextMeta.length < validFiles.length) {
        toast.error("One or more images failed to upload. Please try again.");
      }
      if (nextMeta.length > 0) {
        setUploadedFiles((prev) => [...prev, ...nextMeta]);
        setNewUploadedImages((prevImages) => [...prevImages, ...nextImages]);
      }
    } catch (err) {
      console.error("Error uploading files:", err);
      toast.error("Failed to upload files. Please try again.");
    } finally {
      setUploadFileLoader(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    }
  };

  useEffect(() => {
    if (shiftDetails?.uuid) {
      getAttachmentsAndSignatures();
    }
  }, [shiftDetails?.uuid]);

  const getAttachmentsAndSignatures = () => {
    setGetAttachmentsLoader(true);
    apiGetRequest(`${URL_ROSTERING_GET_ALL_ATTACHMENTS}/${shiftDetails?.uuid}`)
      .then((response) => {
        if (response?.data) {
          setAttachmentsAndSignatures({
            attachments: response.data?.data?.attachments || [],
            signatures: response.data?.data?.pt_sign,
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching attachments and signatures:", error);
      })
      .finally(() => {
        setGetAttachmentsLoader(false);
      });
  };

  const [deleteDocumentLoader, setDeleteDocumentLoader] = React.useState(false);
  const handleViewDocument = (item) => {
    const imageUrl = baseURLImage(item?.file_url);
    window.open(imageUrl, "_blank");
  };

  const handleDeleteDocument = (item) => {
    setDeleteDocumentLoader(true);
    const payload = {
      attachment_id: item?.id,
    };
    apiPostRequest(
      `${URL_ROSTERING_DELETE_SINGLE_ATTACHMENT}/${shiftDetails?.uuid}`,
      payload
    )
      .then((response) => {
        toast.success("Document deleted successfully");
        setAttachmentsAndSignatures({
          attachments: response.data?.data?.attachments || [],
          signatures: response.data?.data?.pt_sign,
        });
      })
      .catch((error) => {
        console.error("Error deleting document:", error);
      })
      .finally(() => {
        setDeleteDocumentLoader(false);
      });
  };

  const [uploadingLoader, setUploadingLoader] = useState(false);
  const handleUploadAttachments = () => {
    setUploadingLoader(true);

    const formData = new FormData();
    formData.append("uuid", shiftDetails?.uuid);

    uploadingFilesData.forEach((image, index) => {
      formData.append(`attachments[${index}][key]`, image.key);
      formData.append(`attachments[${index}][ext]`, image.ext);
      formData.append(
        `attachments[${index}][name]`,
        stripFileExtension(newUploadedImages[index]?.name ?? "")
      );
    });

    apiPostRequest(URL_ROSTERING_UPLOAD_DOCUMENTS, formData)
      .then((response) => {
        toast.success("Files uploaded successfully");
        setUploadedFiles([]);
        setNewUploadedImages([]);
        setAttachmentsAndSignatures({
          attachments: response.data?.data?.attachments || [],
          signatures: response.data?.data?.pt_sign,
        });
      })
      .catch((error) => {
        console.error("Error uploading files:", error);
        toast.error("Failed to upload files");
      })
      .finally(() => {
        setUploadingLoader(false);
      });
  };

  const handleDiscard = () => {
    setNewUploadedImages([]);
    setUploadedFiles([]);
    fileInputRef.current.value = null;
  };

  const totalAttachments =
    attachmentsAndSignatures?.attachments?.length + newUploadedImages.length;

  if (getAttachmentsLoader) {
    return (
      <div className="timesheet-tab-content min-h-0">
        <TimesheetAttachmentsTabSkeleton />
      </div>
    );
  }

  return (
    <div className="timesheet-tab-content td-attachments-tab space-y-3">
      <TimesheetDetailCard>
        <TimesheetFieldLabel>{participantLabel} signature</TimesheetFieldLabel>
        <div className="td-signature-box">
          {attachmentsAndSignatures?.signatures ? (
            <img
              src={attachmentsAndSignatures?.signatures || ""}
              alt={`${participantLabel} signature`}
              className="max-h-[100px] w-full object-contain p-2"
            />
          ) : (
            <p className="text-sm font-medium italic text-[#94A3B8]">
              No {participantLabel.toLowerCase()} signature for this shift.
            </p>
          )}
        </div>
      </TimesheetDetailCard>

      <TimesheetDetailCard className="!bg-white">
        <TimesheetTabSectionHeader
          title="Shift attachments"
          action={
            <p className="text-[11px] font-medium text-[#94A3B8]">
              {totalAttachments} file{totalAttachments !== 1 ? "s" : ""} · max 5
            </p>
          }
        />

        <div className="td-attachment-grid">
          {attachmentsAndSignatures?.attachments?.map((item, index) => (
            <div key={index} className="group relative">
              <div className="td-attachment-thumb">
                <img src={FileIcon} alt="file" className="h-8 w-8 opacity-70" />
              </div>
              <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  className="rounded-full bg-[#4A6BB6] p-1.5 text-white"
                  type="button"
                  onClick={() => handleViewDocument(item)}
                >
                  <FaEye className="h-3 w-3" />
                </button>
                {deleteDocumentLoader ? (
                  <Loader className="h-4 w-4 text-white" />
                ) : (
                  <button
                    className="rounded-full bg-[#FC466C] p-1.5 text-white"
                    type="button"
                    onClick={() => handleDeleteDocument(item)}
                  >
                    <FaTrash className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          ))}

          {newUploadedImages.map((image, index) => (
            <div key={`new-uploaded-${index}`} className="td-attachment-thumb">
              <FaImage className="h-6 w-6 text-[#94A3B8]" />
              <p
                className="max-w-[76px] truncate text-[10px] font-medium text-[#64748B]"
                title={image?.name}
              >
                {image?.name}
              </p>
            </div>
          ))}

          <button
            type="button"
            className="td-attachment-thumb td-attachment-upload"
            onClick={() => fileInputRef.current?.click()}
            disabled={
              uploadFileLoader || uploadingLoader || deleteDocumentLoader
            }
          >
            {uploadFileLoader ? (
              <Loader className="h-5 w-5 text-[#4A6BB6]" />
            ) : (
              <>
                <span className="text-lg font-bold text-[#4A6BB6]">+</span>
                <span className="text-[10px] font-semibold">Upload</span>
              </>
            )}
          </button>

          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileChange}
            accept="image/*"
            multiple
          />
        </div>

        {attachmentsAndSignatures?.attachments?.length === 0 &&
          newUploadedImages.length === 0 && (
            <p className="text-sm font-medium text-[#94A3B8]">
              No attachments available
            </p>
          )}
      </TimesheetDetailCard>

      <div className="td-att-action-strip">
        <CustomButton
          label="Discard"
          cssClass={`px-4 ${
            newUploadedImages?.length > 0
              ? "bg-red-500 text-white"
              : "bg-gray-100 border-none cursor-not-allowed text-gray-700"
          }`}
          type="button"
          variant={null}
          clickHandler={handleDiscard}
          isDisabled={uploadingFilesData?.length === 0}
        />
        <button
          type="button"
          className="td-detail-action-btn td-detail-action-btn--save"
          disabled={
            uploadingLoader ||
            deleteDocumentLoader ||
            newUploadedImages?.length === 0 ||
            uploadingFilesData?.length === 0
          }
          onClick={handleUploadAttachments}
        >
          {(uploadingLoader || deleteDocumentLoader) && (
            <Loader cssClass="!mr-0 h-4 w-4" isSmallView />
          )}
          Save
        </button>
      </div>
    </div>
  );
};

export default Attachments;
